import { Switch } from '@mui/material';
import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AppContext } from '../../../../AppContext';
import { Actions } from '../../../../enums/ActionEnums';
import { Types } from '../../../../enums/TypeEnums';
import {
  useCompanyContactsQuery,
  useDeleteContactMutation,
  IContact,
  useUpdateCompanyMainContactMutation
} from '../../../../generated/graphql';
import { canUserEdit } from '../../../../utils/rightUtils';
import DenseTable from '../../../Common/Tables/DenseTable';
import { IHeadCell } from '../../../Common/Tables/OverviewTable';

interface IProps {
  companyId: number;
}

const CompanyContactsTab: React.FunctionComponent<IProps> = ({ companyId }: IProps) => {
  const { data, loading } = useCompanyContactsQuery({
    variables: { company_id: companyId },
    fetchPolicy: 'no-cache'
  });
  const { dispatch, state } = useContext(AppContext);
  const history = useNavigate();
  const [deleteContact] = useDeleteContactMutation();
  const [contacts, setContacts] = useState<IContact[]>([]);
  const [updateMainContact] = useUpdateCompanyMainContactMutation();
  const canEdit = canUserEdit(state.user, Types.Company);

  const handleDetails = (rowData: IContact) => {
    dispatch({
      type: Actions.SetSelectedContact,
      payload: { name: rowData.fullName, company_name: rowData.company.name }
    });
    history(`/contacts/${rowData.id}`, { state: { ...rowData } });
  };

  const handleRemove = (rowData: IContact) => {
    dispatch({
      type: Actions.ShowConfirmation,
      payload: {
        text: `Are you sure you want to delete ${rowData.fullName}?`,
        agreeAction: handleDelete,
        params: rowData
      }
    });
  };

  useEffect(() => {
    if (data?.result) {
      setContacts(data.result as never[]);
    }
  }, [data]);

  const handleDelete = (rowData: IContact) => {
    deleteContact({
      variables: { id: parseInt(rowData.id) },
      update(_, { data }) {
        dispatch({
          type: Actions.HideConfirmation
        });
        if (data?.result) {
          dispatch({
            type: Actions.ShowMessage,
            payload: {
              severity: 'success',
              text: `Successfully deleted ${rowData.fullName}`,
              autoHide: 4000
            }
          });
          const filteredContacts = Array.from(contacts as IContact[]);
          const index = filteredContacts.findIndex((a) => a?.id === rowData.id);
          filteredContacts.splice(index, 1);
          //const filteredContacts = contacts?.filter((a) => a?.id !== rowData.id);

          setContacts(filteredContacts);
        } else {
          dispatch({
            type: Actions.ShowConfirmation,
            payload: {
              text: 'This is company last contact. Are you sure you want to delete it?',
              agreeAction: deleteLastContact,
              params: rowData
            }
          });
        }
      }
    });
  };

  const deleteLastContact = (rowData: IContact) => {
    deleteContact({
      variables: { id: parseInt(rowData.id), can_delete_last: true },
      update(_, { data }) {
        dispatch({
          type: Actions.HideConfirmation
        });
        if (data?.result) {
          dispatch({
            type: Actions.ShowMessage,
            payload: {
              severity: 'success',
              text: `Successfully deleted ${rowData.fullName}`,
              autoHide: 4000
            }
          });
          const filteredContacts = Array.from(contacts as IContact[]);
          const index = filteredContacts.findIndex((a) => a?.id);
          filteredContacts.splice(index, 1);

          setContacts(filteredContacts);
        }
      }
    });
  };

  const isMainStatus = (rowData: IContact) => (
    <Switch
      id="is_main_contact_switch"
      data-testid="is_main_contact_switch"
      disableRipple
      name="checkIsMainContact"
      onChange={() => handleMainChange(rowData)}
      checked={rowData.is_main || false}
      disabled={!canEdit}
    />
  );

  const handleMainChange = (rowData: IContact) => {
    const is_main = !rowData.is_main;
    updateMainContact({
      variables: {
        id: companyId,
        contact_id: parseInt(rowData.id),
        is_main
      },
      update() {
        const message = is_main
          ? `${rowData.fullName} set as main contact`
          : `${rowData.fullName} no longer main`;
        const updatedContacts = contacts?.map((c) =>
          c?.id === rowData.id ? { ...c, is_main } : c
        );

        dispatch({
          type: Actions.ShowMessage,
          payload: {
            severity: 'success',
            text: message
          }
        });
        setContacts(updatedContacts);
      }
    }).catch((e) =>
      dispatch({
        type: Actions.ShowMessage,
        payload: {
          severity: 'error',
          text: e.message
        }
      })
    );
  };

  const columns = [
    {
      title: 'Details',
      sorting: false,
      width: 68
    },
    {
      title: 'ID',
      field: 'id',
      width: 52,
      type: 'numeric'
    },
    {
      title: 'Main',
      field: 'is_main',
      sorting: false,
      render: isMainStatus,
      width: 71,
      type: 'boolean'
    },
    {
      title: 'Name',
      field: 'fullName',
      width: 277
    },
    {
      title: 'Title General',
      field: 'titleInternal.name',
      width: 209
    },
    {
      title: 'Title',
      field: 'title_external',
      width: 110
    },
    {
      title: 'Address',
      field: 'address.name',
      width: 234
    },
    {
      title: 'Email',
      field: 'email',
      width: 295
    },
    {
      title: 'Remove',
      sorting: false,
      width: 73
    }
  ] as IHeadCell[];

  return (
    <DenseTable
      columns={columns}
      data={contacts}
      loading={loading}
      handleDetails={handleDetails}
      handleRemove={handleRemove}
      crudType={Types.Company}
    />
  );
};

export default CompanyContactsTab;
